/**
 * Order Form Module
 * Handles order form initialization and interactions
 */

import { CurrencyFormatter } from '../utils/currency-formatter';
import { initTomSelect } from './tom-select';
import {
  CurrencyDataSource,
  CurrencyInfo,
  calculateItemTotals as sharedCalculateItemTotals,
  initSidebarNavigation,
} from './document-form-shared';

interface AddressDto {
  Id: number;
  Street: string | null;
  City: string | null;
  ZipCode: string | null;
  Country: string | null;
}

interface CustomerAddressesModel {
  InvoiceAddress: AddressDto | null;
  DeliveryAddress: AddressDto | null;
}

/**
 * Order form state and methods
 */
class OrderFormManager {
  private orderItemsContainer: HTMLElement;
  private template: HTMLTemplateElement;
  private addButtons: NodeListOf<HTMLButtonElement>;
  private emptyState: HTMLElement | null;
  private bottomAddButtonWrapper: HTMLElement | null;
  private itemCounter = 0;
  private currencies: CurrencyDataSource = {};
  private productWeights: Record<string, number> = {};
  private currentCurrencySelect: HTMLSelectElement | null;
  private isHandlingDeliveryChange = false;
  private draggedItemRow: HTMLElement | null = null;

  constructor() {
    this.orderItemsContainer = document.getElementById('orderItemsContainer') as HTMLElement;
    this.template = document.querySelector('template') as HTMLTemplateElement;
    this.addButtons = document.querySelectorAll('.js-order-add-item-btn');
    this.emptyState = document.getElementById('emptyState');
    this.bottomAddButtonWrapper = document.getElementById('addOrderItemBottomWrapper');
    this.currentCurrencySelect = document.querySelector('[name="CurrencyId"]') as HTMLSelectElement;

    if (!this.orderItemsContainer || !this.template || this.addButtons.length === 0) {
      throw new Error('Required order form elements not found');
    }

    this.init();
  }

  private init(): void {
    // Load currency data from section-items element
    this.loadCurrencyData();

    // Set up event listeners
    this.addButtons.forEach((button) => {
      button.addEventListener('click', () => this.addOrderItem());
    });

    // Listen for currency changes
    if (this.currentCurrencySelect) {
      this.currentCurrencySelect.addEventListener('change', () => this.onCurrencyChange());
    }

    // Listen for customer changes
    this.initCustomerChangeHandler();

    this.initProductionDateSync();
    this.initSortableItems();

    // Count existing items
    this.itemCounter = this.orderItemsContainer.querySelectorAll('.order-item-row').length;

    // Initialize existing items rendered by Razor
    this.initializeExistingItems();
    this.syncItemOrders();
    this.updateAddButtonVisibility();
  }

  private initProductionDateSync(): void {
    const productionDateInput = document.querySelector(
      '[name="ProductionDate"]'
    ) as HTMLInputElement;
    const sentDateInput = document.querySelector('[name="SentDate"]') as HTMLInputElement;

    if (!productionDateInput || !sentDateInput) {
      return;
    }

    const syncSentDate = () => {
      sentDateInput.value = productionDateInput.value;
    };

    productionDateInput.addEventListener('input', syncSentDate);
  }

  private initSortableItems(): void {
    this.orderItemsContainer.addEventListener('dragover', (event) =>
      this.handleItemDragOver(event)
    );
    this.orderItemsContainer.addEventListener('drop', (event) => this.handleItemDrop(event));
  }

  private initCustomerChangeHandler(): void {
    const customerSelect = document.querySelector('[name="CustomerId"]') as HTMLSelectElement;

    if (!customerSelect) {
      return;
    }

    customerSelect.addEventListener('change', async () => {
      const customerId = customerSelect.value;

      if (!customerId) {
        return;
      }

      await this.loadCustomerAddresses(parseInt(customerId));
    });
  }

  private async loadCustomerAddresses(customerId: number): Promise<void> {
    try {
      // Get the API URL from data attribute
      const sectionCustomer = document.getElementById('section-customer');
      const apiUrl = sectionCustomer?.getAttribute('data-addresses-url');

      if (!apiUrl) {
        // eslint-disable-next-line no-console
        console.error('Addresses API URL not found');
        return;
      }

      const response = await fetch(`${apiUrl}?customerId=${customerId}`);

      if (!response.ok) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch customer addresses');
        return;
      }

      const data: CustomerAddressesModel = await response.json();

      this.fillAddressFields('DeliveryAddress', data.DeliveryAddress);
      this.fillAddressFields('InvoiceAddress', data.InvoiceAddress);

      // Uncheck "Same as delivery" checkbox
      const sameAsDeliveryCheckbox = document.querySelector(
        '[name="InvoiceSameAsDelivery"]'
      ) as HTMLInputElement;
      if (sameAsDeliveryCheckbox) {
        sameAsDeliveryCheckbox.checked = false;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading customer addresses:', error);
    }
  }

  private fillAddressFields(prefix: string, address: AddressDto | null): void {
    const streetInput = document.querySelector(`[name="${prefix}.Street"]`) as HTMLInputElement;
    const cityInput = document.querySelector(`[name="${prefix}.City"]`) as HTMLInputElement;
    const zipCodeInput = document.querySelector(`[name="${prefix}.ZipCode"]`) as HTMLInputElement;
    const countryInput = document.querySelector(`[name="${prefix}.Country"]`) as HTMLInputElement;

    if (streetInput) streetInput.value = address?.Street || '';
    if (cityInput) cityInput.value = address?.City || '';
    if (zipCodeInput) zipCodeInput.value = address?.ZipCode || '';
    if (countryInput) countryInput.value = address?.Country || '';
  }

  private initializeExistingItems(): void {
    const existingItems = this.orderItemsContainer.querySelectorAll('.order-item-row');

    existingItems.forEach((row) => {
      this.attachItemEventListeners(row as HTMLElement);
    });
  }

  private attachItemEventListeners(row: HTMLElement): void {
    const productSelect = row.querySelector('.item-product') as HTMLSelectElement;
    const quantityInput = row.querySelector('.item-quantity') as HTMLInputElement;
    const priceInput = row.querySelector('.item-price') as HTMLInputElement;
    const vatInput = row.querySelector('.item-vat') as HTMLInputElement;
    const saleInput = row.querySelector('.item-sale') as HTMLInputElement;
    const removeBtn = row.querySelector('.remove-item-btn') as HTMLButtonElement;
    const dragHandle = row.querySelector('.js-item-drag-handle') as HTMLElement;

    // Set up event listeners
    productSelect?.addEventListener('change', () =>
      this.onProductChange(productSelect, priceInput, vatInput)
    );
    quantityInput?.addEventListener('input', () => this.calculateItemTotals(row));
    priceInput?.addEventListener('input', () => this.calculateItemTotals(row));
    vatInput?.addEventListener('input', () => this.calculateItemTotals(row));
    saleInput?.addEventListener('input', () => this.calculateItemTotals(row));
    removeBtn?.addEventListener('click', () => this.removeOrderItem(row));
    dragHandle?.addEventListener('dragstart', (event) => this.handleItemDragStart(event, row));
    dragHandle?.addEventListener('dragend', () => this.resetDraggedItemState());

    initTomSelect(row);

    // Calculate initial totals
    this.calculateItemTotals(row);
  }

  private loadCurrencyData(): void {
    const sectionItems = document.getElementById('section-items');
    if (sectionItems) {
      const currenciesData = sectionItems.getAttribute('data-currencies');
      if (currenciesData) {
        try {
          this.currencies = JSON.parse(currenciesData);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to parse currency data:', error);
        }
      }

      const weightsData = sectionItems.getAttribute('data-product-weights');
      if (weightsData) {
        try {
          this.productWeights = JSON.parse(weightsData);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to parse product weights data:', error);
        }
      }
    }
  }

  private getCurrentCurrency(): CurrencyInfo {
    const selectedCurrencyId = this.currentCurrencySelect?.value || '1';
    return (
      this.currencies[selectedCurrencyId] || {
        Code: 'CZK',
        Culture: 'cs-CZ',
        FreeDeliveryLimit: Number.MAX_VALUE,
        ProductVatPercent: 0,
        DeliveryVatPercent: 0,
      }
    );
  }

  private formatPrice(amount: number): string {
    const currency = this.getCurrentCurrency();
    return CurrencyFormatter.formatPrice(amount, currency.Code, currency.Culture);
  }

  private addOrderItem(): void {
    // Hide empty state
    if (this.emptyState) {
      this.emptyState.style.display = 'none';
    }

    // Clone template
    const clone = this.template.content.cloneNode(true) as DocumentFragment;
    const row = clone.querySelector('.order-item-row') as HTMLElement;

    // Replace INDEX placeholder with current counter
    const index = this.itemCounter++;
    row.innerHTML = row.innerHTML.replace(/INDEX/g, index.toString());

    // Add to container
    this.orderItemsContainer.appendChild(row);

    // New items default to the currently selected currency's product VAT rate
    const vatInput = row.querySelector('.item-vat') as HTMLInputElement | null;
    if (vatInput) {
      vatInput.value = (this.getCurrentCurrency().ProductVatPercent ?? 0).toString();
    }

    // Attach event listeners and initialize
    this.attachItemEventListeners(row);
    this.syncItemOrders();
    this.updateAddButtonVisibility();
  }

  private handleItemDragStart(event: DragEvent, row: HTMLElement): void {
    this.draggedItemRow = row;
    row.classList.add('is-dragging');

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', row.dataset.itemOrder ?? '');
    }
  }

  private handleItemDragOver(event: DragEvent): void {
    if (!this.draggedItemRow) {
      return;
    }

    event.preventDefault();

    const targetRow = (event.target as HTMLElement).closest(
      '.order-item-row'
    ) as HTMLElement | null;
    if (!targetRow || targetRow === this.draggedItemRow) {
      return;
    }

    const targetRect = targetRow.getBoundingClientRect();
    const insertAfter = event.clientY >= targetRect.top + targetRect.height / 2;
    const referenceNode = insertAfter ? targetRow.nextElementSibling : targetRow;

    this.orderItemsContainer.insertBefore(this.draggedItemRow, referenceNode);
  }

  private handleItemDrop(event: DragEvent): void {
    if (!this.draggedItemRow) {
      return;
    }

    event.preventDefault();
    this.syncItemOrders();
  }

  private resetDraggedItemState(): void {
    if (this.draggedItemRow) {
      this.draggedItemRow.classList.remove('is-dragging');
      this.draggedItemRow = null;
    }

    this.syncItemOrders();
  }

  private syncItemOrders(): void {
    const rows = this.orderItemsContainer.querySelectorAll<HTMLElement>('.order-item-row');

    rows.forEach((row, index) => {
      row.dataset.itemOrder = index.toString();

      const itemOrderInput = row.querySelector('.item-order-input') as HTMLInputElement | null;
      if (itemOrderInput) {
        itemOrderInput.value = index.toString();
      }

      // Re-index name, id, and for attributes so ASP.NET Core model binding receives
      // consecutive zero-based indices even after removes or reorders.
      row.querySelectorAll<HTMLElement>('[name], [id], [for]').forEach((el) => {
        const name = el.getAttribute('name');
        if (name) {
          el.setAttribute('name', name.replace(/Items\[\d+\]/, `Items[${index}]`));
        }
        const id = el.getAttribute('id');
        if (id) {
          el.setAttribute('id', id.replace(/Items_(\d+)__/, `Items_${index}__`));
        }
        const forAttr = el.getAttribute('for');
        if (forAttr) {
          el.setAttribute('for', forAttr.replace(/Items_(\d+)__/, `Items_${index}__`));
        }
      });
    });
  }

  private onProductChange(
    select: HTMLSelectElement,
    priceInput: HTMLInputElement,
    vatInput: HTMLInputElement
  ): void {
    const selectedOption = select.options[select.selectedIndex];

    // Get price and VAT from data attributes
    const price = selectedOption.getAttribute('data-price');
    const vat = selectedOption.getAttribute('data-vat');

    if (price && priceInput) {
      priceInput.value = price;
    }

    if (vat && vatInput) {
      vatInput.value = vat;
    }

    // Recalculate totals
    const row = select.closest('.order-item-row') as HTMLElement;
    if (row) {
      // Update the unit weight display from product weights dictionary
      const productId = select.value;
      const unitWeightElement = row.querySelector('.item-unit-weight') as HTMLElement;
      const weightGrams = productId ? this.productWeights[productId] : undefined;
      if (unitWeightElement) {
        unitWeightElement.textContent = weightGrams ? `${weightGrams} g` : '—';
      }
      const unitWeightInput = row.querySelector('.item-unit-weight-input') as HTMLInputElement;
      if (unitWeightInput) {
        unitWeightInput.value = weightGrams ? weightGrams.toString() : '';
      }

      this.calculateItemTotals(row);
    }
  }

  private calculateItemTotals(row: HTMLElement): void {
    sharedCalculateItemTotals(row, (amount) => this.formatPrice(amount));
    this.calculateWeightInfo(row);
    // Update order summary
    this.updateOrderSummary();
  }

  private calculateWeightInfo(row: HTMLElement): void {
    const productSelect = row.querySelector('.item-product') as HTMLSelectElement;
    const quantityInput = row.querySelector('.item-quantity') as HTMLInputElement;
    const priceInput = row.querySelector('.item-price') as HTMLInputElement;
    const pricePerKgElement = row.querySelector('.item-price-per-kg') as HTMLElement;
    const totalWeightElement = row.querySelector('.item-total-weight') as HTMLElement;

    const productId = productSelect?.value;
    const weightGrams = productId ? this.productWeights[productId] : undefined;
    const quantity = parseFloat(quantityInput?.value || '0');
    const pricePerUnit = parseFloat(priceInput?.value || '0');

    if (pricePerKgElement) {
      if (weightGrams && weightGrams > 0) {
        const pricePerKg = pricePerUnit / (weightGrams / 1000);
        pricePerKgElement.textContent = this.formatPrice(pricePerKg);
      } else {
        pricePerKgElement.textContent = '—';
      }
    }

    if (totalWeightElement) {
      if (weightGrams && weightGrams > 0 && quantity > 0) {
        const totalKg = (weightGrams / 1000) * quantity;
        totalWeightElement.textContent = `${totalKg.toFixed(3)} kg`;
      } else {
        totalWeightElement.textContent = '—';
      }
    }
  }

  private removeOrderItem(row: HTMLElement): void {
    row.remove();

    // Show empty state if no items left
    const remainingItems = this.orderItemsContainer.querySelectorAll('.order-item-row');
    if (remainingItems.length === 0 && this.emptyState) {
      this.emptyState.style.display = 'block';
    }

    this.syncItemOrders();
    this.updateAddButtonVisibility();

    // Update order summary
    this.updateOrderSummary();
  }

  private updateAddButtonVisibility(): void {
    const hasItems = this.orderItemsContainer.querySelectorAll('.order-item-row').length > 0;
    this.bottomAddButtonWrapper?.classList.toggle('d-none', !hasItems);
  }

  private updateOrderSummary(): void {
    const items = this.orderItemsContainer.querySelectorAll('.order-item-row');
    let totalPriceWithoutVat = 0;
    let totalVat = 0;
    let totalSalesAmount = 0;

    items.forEach((item) => {
      const quantityInput = item.querySelector('.item-quantity') as HTMLInputElement;
      const priceInput = item.querySelector('.item-price') as HTMLInputElement;
      const vatInput = item.querySelector('.item-vat') as HTMLInputElement;
      const saleInput = item.querySelector('.item-sale') as HTMLInputElement;

      const quantity = parseFloat(quantityInput?.value || '0');
      const pricePerUnit = parseFloat(priceInput?.value || '0');
      const vatPercent = parseFloat(vatInput?.value || '0');
      const salePercent = parseFloat(saleInput?.value || '0');

      const discountedPricePerUnit = pricePerUnit * (1 - salePercent / 100);
      const itemTotalWithoutVat = quantity * discountedPricePerUnit;
      const itemVat = itemTotalWithoutVat * (vatPercent / 100);
      const itemSalesAmount = quantity * pricePerUnit * (salePercent / 100);

      totalPriceWithoutVat += itemTotalWithoutVat;
      totalVat += itemVat;
      totalSalesAmount += itemSalesAmount;
    });

    // Check and auto-toggle free delivery based on items total (skip if user is manually toggling delivery)
    if (!this.isHandlingDeliveryChange) {
      this.checkFreeDeliveryEligibility(totalPriceWithoutVat);
    }

    // Add delivery costs (using the potentially updated checkbox state)
    const deliveryPrice = parseFloat(
      (document.getElementById('deliveryPrice') as HTMLInputElement)?.value || '0'
    );
    const deliveryVat = parseFloat(
      (document.getElementById('deliveryVat') as HTMLInputElement)?.value || '0'
    );
    const freeDeliveryToggle = document.querySelector(
      '.free-delviery-toggle-js'
    ) as HTMLInputElement;

    if (!freeDeliveryToggle?.checked) {
      totalPriceWithoutVat += deliveryPrice;
      totalVat += deliveryPrice * (deliveryVat / 100);
    }

    const totalPrice = totalPriceWithoutVat + totalVat;

    // Update summary elements
    const summaryPriceWithoutVat = document.getElementById('summaryPriceWithoutVat');
    const summaryVatElement = document.getElementById('summaryVat');
    const summarySalesAmountElement = document.getElementById('summarySalesAmount');
    const summaryTotal = document.getElementById('summaryTotal');
    const orderSummary = document.getElementById('orderSummary');

    if (summaryPriceWithoutVat) {
      summaryPriceWithoutVat.textContent = this.formatPrice(totalPriceWithoutVat);
    }

    if (summaryVatElement) {
      summaryVatElement.textContent = this.formatPrice(totalVat);
    }

    if (summarySalesAmountElement) {
      summarySalesAmountElement.textContent = this.formatPrice(totalSalesAmount);
    }

    if (summaryTotal) {
      summaryTotal.textContent = this.formatPrice(totalPrice);
    }

    // Show/hide summary based on whether there are items
    if (orderSummary) {
      orderSummary.classList.toggle('d-none', items.length === 0);
    }
  }

  private onCurrencyChange(): void {
    const currency = this.getCurrentCurrency();

    // Re-prefill delivery VAT and every product line's VAT with the new currency's default
    const deliveryVatInput = document.getElementById('deliveryVat') as HTMLInputElement | null;
    if (deliveryVatInput) {
      deliveryVatInput.value = (currency.DeliveryVatPercent ?? 0).toString();
    }

    const items = this.orderItemsContainer.querySelectorAll('.order-item-row');
    items.forEach((item) => {
      const vatInput = item.querySelector('.item-vat') as HTMLInputElement | null;
      if (vatInput) {
        vatInput.value = (currency.ProductVatPercent ?? 0).toString();
      }
      this.calculateItemTotals(item as HTMLElement);
    });

    // Recalculate delivery totals
    this.calculateDeliveryTotals();
  }

  private updateDeliveryDisplay(): void {
    const deliveryPrice = parseFloat(
      (document.getElementById('deliveryPrice') as HTMLInputElement)?.value || '0'
    );
    const deliveryVat = parseFloat(
      (document.getElementById('deliveryVat') as HTMLInputElement)?.value || '0'
    );
    const deliveryTotalWithVat = document.getElementById('deliveryTotalWithVat');
    const freeDeliveryToggle = document.querySelector(
      '.free-delviery-toggle-js'
    ) as HTMLInputElement;

    if (deliveryTotalWithVat) {
      if (freeDeliveryToggle?.checked) {
        deliveryTotalWithVat.innerHTML = `<span class="delivery-free-indicator">${this.formatPrice(0)}</span>`;
      } else {
        const totalWithVat = deliveryPrice * (1 + deliveryVat / 100);
        deliveryTotalWithVat.textContent = this.formatPrice(totalWithVat);
      }
    }
  }

  private calculateDeliveryTotals(): void {
    this.updateDeliveryDisplay();
    // Update order summary
    this.updateOrderSummary();
  }

  private checkFreeDeliveryEligibility(itemsTotalWithoutVat: number): void {
    const freeDeliveryToggle = document.querySelector(
      '.free-delviery-toggle-js'
    ) as HTMLInputElement;

    if (!freeDeliveryToggle) {
      return;
    }

    // Get current currency's free delivery limit
    const currency = this.getCurrentCurrency();
    const freeDeliveryLimit = currency.FreeDeliveryLimit || Number.MAX_VALUE;

    // Auto-toggle based on limit
    const shouldBeFree = freeDeliveryLimit > 0 && itemsTotalWithoutVat >= freeDeliveryLimit;

    if (shouldBeFree !== freeDeliveryToggle.checked) {
      freeDeliveryToggle.click();

      // Update delivery display with new state
      this.updateDeliveryDisplay();
    }
  }

  public initDeliveryHandlers(): void {
    const freeDeliveryToggle = document.querySelector(
      '.free-delviery-toggle-js'
    ) as HTMLInputElement;
    const deliveryPrice = document.getElementById('deliveryPrice') as HTMLInputElement;
    const deliveryVat = document.getElementById('deliveryVat') as HTMLInputElement;

    freeDeliveryToggle?.addEventListener('change', () => {
      this.isHandlingDeliveryChange = true;

      if (freeDeliveryToggle.checked) {
        deliveryPrice?.classList.add('delivery-price-crossed');
        deliveryVat?.classList.add('delivery-price-crossed');
        if (deliveryPrice) deliveryPrice.readOnly = true;
        if (deliveryVat) deliveryVat.readOnly = false;
      } else {
        deliveryPrice?.classList.remove('delivery-price-crossed');
        deliveryVat?.classList.remove('delivery-price-crossed');
        if (deliveryPrice) deliveryPrice.readOnly = false;
        if (deliveryVat) deliveryVat.readOnly = false;
      }

      this.calculateDeliveryTotals();

      this.isHandlingDeliveryChange = false;
    });

    deliveryPrice?.addEventListener('input', () => {
      if (!freeDeliveryToggle?.checked) {
        this.isHandlingDeliveryChange = true;
        this.calculateDeliveryTotals();
        this.isHandlingDeliveryChange = false;
      }
    });

    deliveryVat?.addEventListener('input', () => {
      if (!freeDeliveryToggle?.checked) {
        this.isHandlingDeliveryChange = true;
        this.calculateDeliveryTotals();
        this.isHandlingDeliveryChange = false;
      }
    });
  }

  public initInvoiceSameAsDeliveryHandler(): void {
    const sameAsDeliveryCheckbox = document.querySelector(
      '[name="InvoiceSameAsDelivery"]'
    ) as HTMLInputElement;

    if (!sameAsDeliveryCheckbox) {
      return;
    }

    // Get delivery and invoice address fields
    const deliveryStreet = document.querySelector(
      '[name="DeliveryAddress.Street"]'
    ) as HTMLInputElement;
    const deliveryCity = document.querySelector(
      '[name="DeliveryAddress.City"]'
    ) as HTMLInputElement;
    const deliveryZipCode = document.querySelector(
      '[name="DeliveryAddress.ZipCode"]'
    ) as HTMLInputElement;
    const deliveryCountry = document.querySelector(
      '[name="DeliveryAddress.Country"]'
    ) as HTMLInputElement;

    const invoiceStreet = document.querySelector(
      '[name="InvoiceAddress.Street"]'
    ) as HTMLInputElement;
    const invoiceCity = document.querySelector('[name="InvoiceAddress.City"]') as HTMLInputElement;
    const invoiceZipCode = document.querySelector(
      '[name="InvoiceAddress.ZipCode"]'
    ) as HTMLInputElement;
    const invoiceCountry = document.querySelector(
      '[name="InvoiceAddress.Country"]'
    ) as HTMLInputElement;

    const invoiceAddressFields = document.getElementById('invoiceAddressFields');

    const copyDeliveryToInvoice = () => {
      if (invoiceStreet && deliveryStreet) invoiceStreet.value = deliveryStreet.value;
      if (invoiceCity && deliveryCity) invoiceCity.value = deliveryCity.value;
      if (invoiceZipCode && deliveryZipCode) invoiceZipCode.value = deliveryZipCode.value;
      if (invoiceCountry && deliveryCountry) invoiceCountry.value = deliveryCountry.value;
    };

    const toggleInvoiceFields = (disable: boolean) => {
      if (invoiceAddressFields) {
        invoiceAddressFields.style.display = disable ? 'none' : 'block';
      }
    };

    // Handle checkbox change
    sameAsDeliveryCheckbox.addEventListener('change', () => {
      if (sameAsDeliveryCheckbox.checked) {
        copyDeliveryToInvoice();
        toggleInvoiceFields(true);
      } else {
        toggleInvoiceFields(false);
      }
    });

    // Also listen to delivery address changes when checkbox is checked
    const deliveryInputs = [deliveryStreet, deliveryCity, deliveryZipCode, deliveryCountry];
    deliveryInputs.forEach((input) => {
      input?.addEventListener('input', () => {
        if (sameAsDeliveryCheckbox.checked) {
          copyDeliveryToInvoice();
        }
      });
    });

    // Initialize state on load
    if (sameAsDeliveryCheckbox.checked) {
      copyDeliveryToInvoice();
      toggleInvoiceFields(true);
    }
  }
}

/**
 * Initialize order form functionality
 * Only runs if .order-form-js element exists in the document
 */
export async function initOrderForm(container: Document | HTMLElement = document): Promise<void> {
  // Set up event listener for form replacement (only once)
  if (!initOrderForm.listenerInitialized) {
    document.addEventListener('form-replaced', () => {
      initOrderForm(document);
    });
    initOrderForm.listenerInitialized = true;
  }

  const orderForm = container.querySelector('.order-form-js');

  if (!orderForm) {
    return;
  }

  // Initialize sidebar navigation
  initSidebarNavigation();

  // Initialize order form manager
  const formManager = new OrderFormManager();
  formManager.initDeliveryHandlers();
  formManager.initInvoiceSameAsDeliveryHandler();
}

// Static flag to track if event listener has been initialized
initOrderForm.listenerInitialized = false;
